<template>
  <BaseLayout
    :background="background"
    :style="{ '--sidebar-width': isJobsV2 ? '70px' : '133px' }"
  >
    <template #sidebar>
      <AppSidebar
        v-if="isJobsV2"
        :items="previewSideBarNavs"
      />
      <TalentSideBar
        v-else
        :items="sideBarNavs"
      />
    </template>

    <AppHeader />
    <slot />
    <ModalConfirm
      v-if="isAuthenticated && notificationType === 'confirm'"
      v-model="visible"
      :title="notificationSettings?.title"
      :text="notificationSettings?.text"
      :text-ok="notificationSettings?.textApply"
      :text-cancel="notificationSettings?.textCancel"
      :type="notificationSettings?.type"
      :max-width="notificationSettings?.maxWidth"
      :is-icon="notificationSettings?.hasIcon ?? true"
      :text-center="notificationSettings?.isTextCenter ?? true"
      @confirm="onConfirm"
      @cancel="onCancel"
      @close="onCancel"
    />
    <ModalAlert
      v-if="isAuthenticated && notificationType === 'alert'"
      :visible="visible"
      :text="notificationSettings?.text"
      :text-ok="notificationSettings?.textOk"
      :type="notificationSettings?.type"
      :link="notificationSettings?.link"
      :text-link="notificationSettings?.textLink"
      is-icon
      @close="onCancel"
    />
    <TheCookieBanner />
  </BaseLayout>
</template>

<script>
import { useThemeStore } from '@/store/theme.js'
import { useAlertsStore } from '@/store/alerts.js'
import { useChatsStore } from '@/store/chats.js'
import { useNotificationStore } from '@/store/notification.js'
import BaseLayout from '@/layouts/BaseLayout.vue'
import AppHeader from '@/v2/components/layout/AppHeader.vue'
import AppSidebar from '@/v2/components/layout/AppSidebar.vue'
import TalentSideBar from '@/components/nav/TalentSidebar.vue'
import { useAuthStore } from '@/store/auth.js'
import ModalAlert from '@/components/modals/ModalAlert.vue'
import ModalConfirm from '@/components/modals/ModalConfirm.vue'
import IconSearch from '@/components/icons/IconSearch.vue'
import IconHome from '@/components/icons/IconHome.vue'
import IconUser from '@/components/icons/IconUser.vue'
import { InitUserMixin } from '@/mixins/initUser.mixin.js'
import { useLanguageStore } from '@/store/language.js'
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'
import TheCookieBanner from '@/components/TheCookieBanner.vue'
import IconMailSidebar from '@/components/icons/IconMailSidebar.vue'

export default {
  name: 'DefaultLayout',
  components: {
    TheCookieBanner,
    ModalConfirm,
    ModalAlert,
    BaseLayout,
    AppHeader,
    AppSidebar,
    TalentSideBar
  },
  mixins: [InitUserMixin],
  data() {
    return {
      themeStore: null,
      authStore: null,
      alertsStore: null,
      chatsStore: null,
      notificationStore: null,
      localeStore: null,
      i18n: null
    }
  },
  computed: {
    background() {
      const { background = 'primary' } = this.$route.meta
      return background
    },
    isChatPage() {
      return this.$route.path.includes('notifications/chat')
    },
    visible: {
      get() {
        return this.notificationStore?.visible
      },
      set(value) {
        this.notificationStore?.setVisible(value)
      }
    },
    notificationSettings() {
      return this.notificationStore?.settings
    },
    notificationType() {
      return this.notificationStore?.type
    },
    theme: {
      get() {
        return this.themeStore?.currentTheme
      },
      set(value) {
        this.themeStore.setTheme(value)
      }
    },
    currentLocale() {
      return this.localeStore?.currentLocale
    },
    isJobsV2() {
      return !this.authStore?.user?.features?.vacancy?.jobs
    },
    previewSideBarNavs() {
      const { currentLocale } = this
      return [
        {
          label: this.$t('pages.base.home'),
          icon: 'nav-home',
          link: this.localePath('/', currentLocale)
        },
        {
          label: this.$t('pages.talent.candidate.profile', currentLocale),
          icon: 'nav-user',
          link: this.localePath('/profile', currentLocale)
        },
        {
          label: this.$t('pages.talent.candidate.jobs', currentLocale),
          icon: 'nav-search',
          link: this.localePath('/vacancies', currentLocale)
        },
        {
          label: this.$t('pages.talent.candidate.notifications', currentLocale),
          icon: 'nav-sms',
          badge: this.alertsStore?.count,
          link: this.localePath('/notifications', currentLocale)
        }
      ]
    },
    sideBarNavs() {
      const { currentLocale } = this
      return [
        {
          label: this.$t('pages.base.home'),
          id: 'home',
          icon: IconHome,
          link: this.localePath('/', currentLocale)
        },
        {
          label: this.$t('pages.talent.candidate.profile', currentLocale),
          id: 'profile',
          icon: IconUser,
          link: this.localePath('/profile', currentLocale)
        },
        {
          label: this.$t('pages.talent.candidate.jobs', currentLocale),
          id: 'job',
          icon: IconSearch,
          link: this.localePath('/vacancies', currentLocale)
        },
        {
          label: this.$t('pages.talent.candidate.notifications', currentLocale),
          icon: IconMailSidebar,
          badge: this.alertsStore?.count,
          link: this.localePath('/notifications', currentLocale)
        }
      ]
    },
    titleTemplate() {
      if (!this.alertsStore) return '%s - Rocken Talent'
      return this.alertsStore.count
        ? `(${this.alertsStore.count}) %s - Rocken Talent`
        : '%s - Rocken Talent'
    }
  },
  created() {
    this.themeStore = useThemeStore()
    this.authStore = useAuthStore()
    this.localeStore = useLanguageStore()
    this.i18n = useI18n()

    if (this.isAuthenticated) {
      this.alertsStore = useAlertsStore()
      this.chatsStore = useChatsStore()
      this.notificationStore = useNotificationStore()
      this.alertsStore.init()
    }

    useHead({
      titleTemplate: () => this.titleTemplate,
      htmlAttrs: {
        lang: this.i18n.locale.value
      }
    })
  },
  methods: {
    onConfirm() {
      this.notificationStore.apply()
    },
    onCancel() {
      this.notificationStore.hide()
    }
  }
}
</script>
